import { useState, useRef, useEffect, useCallback } from "react";
import { MessageSquarePlus, X, Bug, Lightbulb, Heart, MessageCircle, Loader2, Paperclip, Send, FileText, Inbox, PenLine } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { MyBetaFeedback } from "@/components/common/MyBetaFeedback";
import { APP_VERSION } from "@/lib/version";

const CATS = [
  { id: "bug", label: "Bug", icon: Bug },
  { id: "idea", label: "Idea", icon: Lightbulb },
  { id: "praise", label: "Praise", icon: Heart },
  { id: "other", label: "Other", icon: MessageCircle },
];
// Backend caps each attachment's base64 at 2,400,000 chars (~1.78MB raw) and allows up to 5.
const MAX_ATTACH = 5;
const MAX_ATTACH_BYTES = Math.floor((2_400_000 / 4) * 3);
const ATTACH_ACCEPT = "image/*,application/pdf,text/plain,text/csv,application/json,.log,.doc,.docx,.xls,.xlsx";

// Floating in-app beta feedback widget → POST /api/beta/feedback (admin beta feedback queue).
export function BetaFeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("new");
  const [attention, setAttention] = useState(0); // resolved reports awaiting the reporter's confirmation
  const [category, setCategory] = useState("bug");
  const [message, setMessage] = useState("");
  const [attachments, setAttachments] = useState([]); // [{ name, mime, data_b64, preview, isImage }]
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  // Poll the reporter's own feedback for resolved-but-unconfirmed items → surfaces a dot on the pill
  // nudging testers back to confirm the fix.
  const refreshAttention = useCallback(async () => {
    try {
      const res = await api.myBetaFeedback();
      setAttention((res?.feedback || []).filter((f) => f.status === "resolved" && !f.confirmed_at).length);
    } catch {
      /* silent — the dot just won't show */
    }
  }, []);

  useEffect(() => {
    refreshAttention();
    const id = setInterval(refreshAttention, 90000);
    return () => clearInterval(id);
  }, [refreshAttention]);

  // Re-check whenever the panel opens (keeps the dot honest after the tester acts elsewhere).
  useEffect(() => {
    if (open) refreshAttention();
  }, [open, refreshAttention]);

  const reset = () => {
    setCategory("bug");
    setMessage("");
    setAttachments([]);
  };

  const onFiles = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const room = MAX_ATTACH - attachments.length;
    if (room <= 0) return toast.error(`You can attach up to ${MAX_ATTACH} files.`);
    if (files.length > room) toast.error(`Only ${room} more file${room === 1 ? "" : "s"} can be attached.`);
    files.slice(0, room).forEach((f) => {
      if (f.size > MAX_ATTACH_BYTES) return toast.error(`"${f.name}" is too large (max 1.7 MB each).`);
      const isImage = f.type.startsWith("image/");
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = String(reader.result || "");
        setAttachments((cur) =>
          cur.length >= MAX_ATTACH
            ? cur
            : [...cur, { name: f.name, mime: f.type || "application/octet-stream", data_b64: dataUrl.split(",")[1] || "", preview: dataUrl, isImage }],
        );
      };
      reader.readAsDataURL(f);
    });
  };

  const removeAttachment = (i) => setAttachments((cur) => cur.filter((_, j) => j !== i));

  const submit = async () => {
    const msg = message.trim();
    if (!msg) return toast.error("Please add a short description.");
    setSending(true);
    try {
      const res = await api.submitBetaFeedback({
        category,
        message: msg,
        screen: window.location.pathname,
        platform: "web",
        app_version: APP_VERSION,
        ...(attachments.length
          ? { attachments: attachments.map((a) => ({ name: a.name, mime: a.mime, data_b64: a.data_b64 })) }
          : {}),
      });
      toast.success("Feedback sent 🎉", {
        description: res?.id ? `Ref ${res.id} · thanks for helping shape the beta.` : "Thanks for helping shape the beta.",
      });
      reset();
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send feedback — please retry.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {open && (
        <div
          className="fixed bottom-36 right-4 z-40 w-[calc(100vw-2rem)] max-w-sm animate-fade-up rounded-3xl border border-white/10 bg-bloq-s1/95 p-5 shadow-2xl backdrop-blur-xl md:bottom-24 md:right-6"
          data-testid="beta-feedback-panel"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-semibold text-white">
              <MessageSquarePlus className="h-4 w-4 text-bloq-mint" /> Beta feedback
            </div>
            <button
              onClick={() => setOpen(false)}
              className="rounded-full p-1 text-bloq-muted transition-colors hover:text-white"
              data-testid="beta-feedback-close"
              aria-label="Close feedback"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="mt-3 flex gap-1 rounded-full border border-white/10 p-0.5" data-testid="beta-feedback-tabs">
            {[
              { id: "new", label: "New", icon: PenLine },
              { id: "mine", label: "My feedback", icon: Inbox },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`beta-feedback-tab-${t.id}`}
                className={cn(
                  "flex flex-1 items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors",
                  tab === t.id ? "bg-bloq-mint/15 text-bloq-mint" : "text-bloq-muted hover:text-white",
                )}
              >
                <t.icon className="h-3.5 w-3.5" /> {t.label}
              </button>
            ))}
          </div>

          {tab === "new" ? (
          <>
          <p className="mt-3 text-xs text-bloq-muted">Found a bug or have an idea? It goes straight to the team.</p>

          <div className="mt-4 grid grid-cols-4 gap-2">
            {CATS.map((c) => (
              <button
                key={c.id}
                onClick={() => setCategory(c.id)}
                data-testid={`beta-feedback-cat-${c.id}`}
                className={cn(
                  "flex flex-col items-center gap-1 rounded-xl border py-2 text-[11px] font-medium transition-colors",
                  category === c.id
                    ? "border-bloq-mint/50 bg-bloq-mint/10 text-bloq-mint"
                    : "border-white/10 text-bloq-muted hover:text-white",
                )}
              >
                <c.icon className="h-4 w-4" />
                {c.label}
              </button>
            ))}
          </div>

          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
            maxLength={2000}
            placeholder="What happened? Steps, what you expected…"
            className="mt-3 w-full resize-none rounded-xl border border-white/12 bg-bloq-bg px-3 py-2.5 text-sm text-white placeholder:text-bloq-muted focus:outline-none focus:ring-1 focus:ring-bloq-mint"
            data-testid="beta-feedback-message"
          />

          {attachments.length > 0 && (
            <div className="mt-2 grid grid-cols-3 gap-2" data-testid="beta-feedback-attachments">
              {attachments.map((a, i) => (
                <div key={i} className="group relative overflow-hidden rounded-xl border border-white/10 bg-bloq-s2" data-testid={`beta-feedback-attach-item-${i}`}>
                  {a.isImage ? (
                    <img src={a.preview} alt={a.name} className="h-16 w-full object-cover" />
                  ) : (
                    <div className="flex h-16 flex-col items-center justify-center gap-1 px-1 text-center">
                      <FileText className="h-5 w-5 text-bloq-mint" />
                      <span className="w-full truncate text-[10px] text-bloq-muted">{a.name}</span>
                    </div>
                  )}
                  <button
                    onClick={() => removeAttachment(i)}
                    className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-bloq-bg/80 text-bloq-muted transition-colors hover:text-white"
                    data-testid={`beta-feedback-remove-attach-${i}`}
                    aria-label="Remove attachment"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {attachments.length < MAX_ATTACH && (
            <button
              onClick={() => fileRef.current?.click()}
              className="mt-2 flex items-center gap-1.5 text-xs text-bloq-muted transition-colors hover:text-bloq-mint"
              data-testid="beta-feedback-attach-btn"
            >
              <Paperclip className="h-3.5 w-3.5" /> Attach files or screenshots ({attachments.length}/{MAX_ATTACH})
            </button>
          )}
          <input ref={fileRef} type="file" accept={ATTACH_ACCEPT} multiple className="hidden" onChange={onFiles} data-testid="beta-feedback-attach" />

          <button
            onClick={submit}
            disabled={sending || !message.trim()}
            className="mt-4 flex h-11 w-full items-center justify-center gap-2 rounded-full bloq-gradient text-sm font-bold text-bloq-bg transition-opacity hover:opacity-90 disabled:opacity-50"
            data-testid="beta-feedback-submit"
          >
            {sending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Send className="h-4 w-4" /> Send feedback
              </>
            )}
          </button>
          </>
          ) : (
            <MyBetaFeedback active={open && tab === "mine"} onConfirmed={refreshAttention} />
          )}
        </div>
      )}

      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Send beta feedback"
        data-testid="beta-feedback-trigger"
        className="fixed bottom-20 right-4 z-40 flex items-center gap-2 rounded-full border border-bloq-mint/30 bg-bloq-s1/90 px-4 py-3 text-sm font-semibold text-bloq-mint shadow-mint backdrop-blur-xl transition-transform hover:scale-105 md:bottom-6 md:right-6"
      >
        <MessageSquarePlus className="h-5 w-5" />
        <span className="hidden sm:inline">Feedback</span>
        {attention > 0 && !open && (
          <span
            className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-bloq-mint px-1 text-[10px] font-bold text-bloq-bg shadow-mint"
            data-testid="beta-feedback-attention-dot"
            title="A report needs your confirmation"
          >
            {attention > 9 ? "9+" : attention}
          </span>
        )}
      </button>
    </>
  );
}
